import { useState } from "react";
import { DataTable } from "@/components/data-tables/data-table";
import { createUserGroupColumns, UserGroupSearchableColumns } from "./UserGroupDataTableColumns";
import { useDataTable } from "@/hooks/useDataTable";
import { Button } from "@/components/ui/button";
import { PlusCircledIcon } from "@radix-ui/react-icons";
import { toast } from "sonner";
import UserGroupDialog from "@/components/dialogs/UserGroupDialog";
import ImportGroupsDialog from "@/components/dialogs/ImportGroupsDialog";
import { z } from "zod";
import { userGroupValidators } from "@shared/validators/admin/user-groups-validators";
import { useDeleteUserGroupMutation, useSyncAllGroupsNowMutation } from "@/api/mutations/admin/UserGroupMutations";
import ManageConnectionsDialog from "@/components/dialogs/ManageConnectionsDialog";
import ManageUsersDialog from "@/components/dialogs/ManageUsersDialog";
import { CloudDownload, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";

type UserGroup = z.infer<typeof userGroupValidators.userGroupSchema>;

interface UserGroupManagementActions {
    onViewDetails?: (userGroup: UserGroup) => void;
    onEdit?: (userGroup: UserGroup) => void;
    onManageUsers?: (userGroup: UserGroup) => void;
    onManageConnections?: (userGroup: UserGroup) => void;
    onDelete?: (userGroup: UserGroup) => void;
}

type UserGroupDataTableProps = {
    data: UserGroup[];
    actions?: UserGroupManagementActions;
};

const UserGroupDataTable = ({ data, actions }: UserGroupDataTableProps) => {
    const { t } = useTranslation();
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [usersDialogOpen, setUsersDialogOpen] = useState(false);
    const [connectionsDialogOpen, setConnectionsDialogOpen] = useState(false);
    const [selectedUserGroup, setSelectedUserGroup] = useState<UserGroup | null>(null);

    const deleteUserGroupMutation = useDeleteUserGroupMutation();
    const syncAllGroupsMutation = useSyncAllGroupsNowMutation();

    // Default action callbacks
    const defaultActions: UserGroupManagementActions = {
        onViewDetails: (userGroup) => {
            toast.info(t("dataTables.viewingDetailsFor", { name: userGroup.name }));
            setSelectedUserGroup(userGroup);
            setEditDialogOpen(true);
        },
        onEdit: (userGroup) => {
            setSelectedUserGroup(userGroup);
            setEditDialogOpen(true);
        },
        onManageUsers: (userGroup) => {
            setSelectedUserGroup(userGroup);
            setUsersDialogOpen(true);
        },
        onManageConnections: (userGroup) => {
            setSelectedUserGroup(userGroup);
            setConnectionsDialogOpen(true);
        },
        onDelete: (userGroup) => {
            if (confirm(t("dataTables.confirmDeleteUserGroup", { name: userGroup.name }))) {
                deleteUserGroupMutation.mutate({ ids: [userGroup.id] });
            }
        },
    };

    const finalActions = actions || defaultActions;
    const columns = createUserGroupColumns(finalActions);

    const { table } = useDataTable({
        columns,
        data,
        pageCount: 1,
        searchableColumns: UserGroupSearchableColumns,
    });

    const rows = table.getSelectedRowModel();
    const ids = rows.flatRows.map((row) => row.original.id) as string[];

    const handleDeleteUserGroups = () => {
        if (confirm(t("dataTables.confirmDeleteUserGroups", { count: ids.length }))) {
            deleteUserGroupMutation.mutate({ ids });
        }
    };

    const handleCreateUserGroup = () => {
        setSelectedUserGroup(null);
        setCreateDialogOpen(true);
    };

    const handleImportGroups = () => {
        setImportDialogOpen(true);
    };

    const handleSyncNow = () => {
        syncAllGroupsMutation.mutate();
    };

    return (
        <>
            <DataTable
                table={table}
                columns={columns}
                searchableColumns={UserGroupSearchableColumns}
                newRowButton={
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="h-8" onClick={handleCreateUserGroup}>
                            <PlusCircledIcon className="mr-2 size-4" aria-hidden="true" />
                            {t("dataTables.newLocalGroup")}
                        </Button>
                        <Button variant="outline" size="sm" className="h-8" onClick={handleImportGroups}>
                            <CloudDownload className="mr-2 size-4" aria-hidden="true" />
                            {t("dataTables.importFromIdp")}
                        </Button>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            className="h-8" 
                            onClick={handleSyncNow}
                            disabled={syncAllGroupsMutation.isPending}
                        >
                            <RefreshCw className={`mr-2 size-4 ${syncAllGroupsMutation.isPending ? 'animate-spin' : ''}`} aria-hidden="true" />
                            {syncAllGroupsMutation.isPending ? t("dataTables.syncing") : t("dataTables.syncNow")}
                        </Button>
                    </div>
                }
                deleteRowsAction={handleDeleteUserGroups}
            />

            {/* Create User Group Dialog */}
            <UserGroupDialog 
                open={createDialogOpen} 
                onOpenChange={setCreateDialogOpen}
                userGroup={null} 
                mode="create" 
            />

            {/* Edit User Group Dialog */}
            <UserGroupDialog
                open={editDialogOpen}
                onOpenChange={setEditDialogOpen}
                userGroup={selectedUserGroup}
                mode="edit"
            />

            {/* Manage Users Dialog */}
            <ManageUsersDialog
                open={usersDialogOpen}
                onOpenChange={setUsersDialogOpen}
                userGroup={selectedUserGroup}
            />

            {/* Manage Connections Dialog */}
            <ManageConnectionsDialog
                open={connectionsDialogOpen}
                onOpenChange={setConnectionsDialogOpen}
                userGroup={selectedUserGroup}
            />

            {/* Import Groups Dialog */}
            <ImportGroupsDialog
                open={importDialogOpen}
                onOpenChange={setImportDialogOpen}
            />
        </>
    );
};

export default UserGroupDataTable;
